import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, FileSearch, FileText, Search, Briefcase, ArrowRight, Clock, Zap, Target, Users, CheckCircle2, FlaskConical } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';

interface UseCase {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  shortDescription: string;
  benefits: string[];
  examples: string[];
}

const useCases: UseCase[] = [
  {
    id: 'contract-review',
    title: 'Contract Review & Analysis',
    icon: Scale,
    shortDescription: 'Quickly identify key terms, risks, obligations, and unusual clauses in contracts. Perfect for business transactions, vendor agreements, and partnership deals.',
    description: 'Contract review is one of the most time-consuming tasks in business. Our AI automates this process, allowing you to quickly identify key terms, risks, obligations, and unusual clauses in any contract. Perfect for business transactions, vendor agreements, partnership deals, and service contracts. Save hours on every contract review while ensuring nothing is missed.',
    benefits: [
      'Identify key terms and obligations in seconds',
      'Spot unusual or risky clauses automatically',
      'Compare contracts across multiple documents',
      'Extract specific provisions (indemnification, termination, etc.)',
      'Review hundreds of contracts in minutes'
    ],
    examples: [
      'What are the termination clauses in this employment contract?',
      'Find all indemnification provisions across these contracts',
      'What are the key dates and deadlines in this M&A agreement?',
      'Identify any non-compete or non-disclosure clauses'
    ]
  },
  {
    id: 'document-analysis',
    title: 'Document Analysis & Review',
    icon: FileSearch,
    shortDescription: 'Analyze reports, contracts, and business documents. Extract key information, identify patterns, and gain insights faster.',
    description: 'Business operations require analyzing massive amounts of documents—reports, contracts, agreements, and records. Our AI helps you find the information you need instantly, identify patterns and trends, and extract key insights. Make informed decisions with confidence, knowing you\'ve found all relevant information.',
    benefits: [
      'Analyze reports and business documents quickly',
      'Extract key information and insights',
      'Identify patterns and trends across documents',
      'Build timelines from document records',
      'Make informed decisions with comprehensive analysis'
    ],
    examples: [
      'Find all mentions of pricing in these contracts',
      'What are the key dates and deadlines in these documents?',
      'What are the main findings in these reports?',
      'Extract all financial information from these documents'
    ]
  },
  {
    id: 'due-diligence',
    title: 'Due Diligence',
    icon: FileText,
    shortDescription: 'Accelerate due diligence reviews for transactions. Analyze hundreds of documents to identify risks, liabilities, and deal-breakers efficiently.',
    description: 'Due diligence requires reviewing hundreds or thousands of documents to identify risks, liabilities, and potential deal-breakers. Our AI accelerates this process dramatically, allowing you to complete thorough due diligence reviews in days instead of weeks. Perfect for business acquisitions, real estate deals, and investment reviews.',
    benefits: [
      'Review hundreds of documents in hours, not weeks',
      'Identify risks and liabilities automatically',
      'Find potential deal-breakers quickly',
      'Extract key financial and legal information',
      'Complete comprehensive due diligence faster'
    ],
    examples: [
      'What are the material liabilities in these documents?',
      'Find all contracts with termination clauses',
      'What are the key financial obligations?',
      'Identify any regulatory compliance issues'
    ]
  },
  {
    id: 'intelligent-search',
    title: 'Intelligent Search & Discovery',
    icon: Search,
    shortDescription: 'Search through your document library, reports, and research materials. Find relevant information and patterns to support your decisions.',
    description: 'Building on past insights is key to making better decisions. Our AI helps you search through your document library, reports, and research materials to find relevant information and patterns. Learn from past projects and strengthen your current work with proven approaches.',
    benefits: [
      'Search your entire document library instantly',
      'Find relevant information and similar documents',
      'Learn from past successful projects',
      'Make better decisions with proven approaches',
      'Maintain a searchable knowledge base'
    ],
    examples: [
      'Find documents similar to this contract',
      'What approaches were successful in similar projects?',
      'Search for information on pricing strategies',
      'Find research reports on this business issue'
    ]
  },
  {
    id: 'compliance',
    title: 'Compliance & Regulatory Review',
    icon: Briefcase,
    shortDescription: 'Review regulatory documents, compliance requirements, and policy changes. Stay ahead of regulatory updates and ensure client compliance.',
    description: 'Staying compliant with ever-changing regulations is challenging. Our AI helps you review regulatory documents, track compliance requirements, and stay ahead of policy changes. Ensure your organization remains compliant and avoid costly violations.',
    benefits: [
      'Review regulatory documents quickly',
      'Track compliance requirements across documents',
      'Stay updated on policy changes',
      'Identify compliance gaps',
      'Ensure organizational adherence to regulations'
    ],
    examples: [
      'What are the compliance requirements in these regulations?',
      'Find all mentions of data privacy requirements',
      'What policy changes affect this client?',
      'Identify any compliance gaps in these documents'
    ]
  },
  {
    id: 'policy-review',
    title: 'Policy & Compliance Review',
    icon: FileText,
    shortDescription: 'Review policies, compliance documents, and regulatory filings. Identify inconsistencies, verify requirements, and ensure documents meet standards.',
    description: 'Policy and compliance review requires careful examination of policies, compliance documents, and regulatory filings to ensure they meet standards and avoid inconsistencies. Our AI helps you review these documents thoroughly, identify potential issues, and verify that all provisions align with organizational requirements.',
    benefits: [
      'Review policy documents thoroughly',
      'Identify inconsistencies and potential issues',
      'Verify provisions align with organizational requirements',
      'Ensure compliance with regulations',
      'Maintain standards with precision and care'
    ],
    examples: [
      'What are the key provisions in this policy?',
      'Identify any inconsistencies in the compliance documents',
      'What are the compliance requirements?',
      'Find all mentions of specific regulations'
    ]
  },
  {
    id: 'scientific-research',
    title: 'Research & Analysis for Scientific Users',
    icon: FlaskConical,
    shortDescription: 'Literature review, paper analysis, and hypothesis discovery with a private LLM. Keep sensitive research data on your infrastructure—ideal for labs, pharma, and academia.',
    description: 'Scientific research generates vast amounts of papers, lab notes, and datasets. Our document AI with private LLM deployment lets you query literature, compare findings, and surface patterns across your corpus without sending data to public clouds. Perfect for research institutions, pharma R&D, and academic labs that require data sovereignty and reproducibility.',
    benefits: [
      'Run AI on papers and lab notes inside your own environment',
      'Literature review and citation discovery at scale',
      'Reproducibility and audit trails with private LLM',
      'No sensitive data leaves your infrastructure',
      'Compliance with research ethics and data governance'
    ],
    examples: [
      'Which papers in our library report similar methodology?',
      'Summarize the main conclusions from these trial reports',
      'Find all mentions of this compound and side effects',
      'What are the conflicting findings across these studies?'
    ]
  }
];

const UseCases: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <PublicHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12 px-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Use Cases</h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
            Discover how teams across industries use our AI to automate their document work and drive results faster
          </p>
        </div>

        <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 px-4">
          {useCases.map((useCase) => {
            const Icon = useCase.icon;
            return (
              <Link
                key={useCase.id}
                to={`/use-cases/${useCase.id}`}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow border-l-4 border-primary-600"
              >
                <Icon className="h-10 w-10 text-primary-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-600 mb-4">{useCase.shortDescription}</p>
                <div className="text-primary-600 font-medium flex items-center">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-8 sm:mb-12 mx-4 sm:mx-0">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center px-4">Why Teams Use Our AI</h2>
          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center">
              <Clock className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Save Time</h3>
              <p className="text-gray-600">
                Reduce document review time by 80%. Focus on high-value work instead of manual research.
              </p>
            </div>
            <div className="text-center">
              <Zap className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Work Faster</h3>
              <p className="text-gray-600">
                Get instant answers to document questions. Speed up decision-making and client responses.
              </p>
            </div>
            <div className="text-center">
              <Target className="h-12 w-12 text-primary-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Never Miss Details</h3>
              <p className="text-gray-600">
                AI-powered analysis catches what human eyes might miss. Make better decisions with confidence.
              </p>
            </div>
          </div>
        </div>

        {/* Practice Areas */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 sm:p-8 text-white mb-8 sm:mb-12 mx-4 sm:mx-0">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 text-center px-4">Perfect for Every Industry</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Business & Finance</h4>
              <p className="text-sm text-primary-100">Contracts, agreements, reports</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Healthcare</h4>
              <p className="text-sm text-primary-100">Medical records, policies, compliance</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Technology</h4>
              <p className="text-sm text-primary-100">Technical docs, agreements, specs</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Real Estate</h4>
              <p className="text-sm text-primary-100">Due diligence, contracts, compliance</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Manufacturing</h4>
              <p className="text-sm text-primary-100">Specs, compliance, quality docs</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Education</h4>
              <p className="text-sm text-primary-100">Policies, research, compliance</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Government</h4>
              <p className="text-sm text-primary-100">Regulations, policies, compliance</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
              <h4 className="font-semibold mb-2">Consulting</h4>
              <p className="text-sm text-primary-100">Reports, analysis, documentation</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-white rounded-lg shadow-md p-6 sm:p-8 mx-4 sm:mx-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4 px-4">Ready to Automate Your Document Work?</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
            Start using our app today and see how much time you can save on document review and analysis.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4">
            <Link
              to="/register"
              className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold"
            >
              Start Free Trial
            </Link>
            <Link
              to="/how-to-use"
              className="inline-block bg-white text-primary-600 px-8 py-3 rounded-lg border-2 border-primary-600 hover:bg-primary-50 transition-colors font-semibold"
            >
              Learn How to Use
            </Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default UseCases;
